import { ApiError } from '@/shared/api/client';
import type { MeResponse } from '@/shared/api/types';

/* Восстановление сессии при открытии админки.
 *
 * Refresh-cookie живёт 30 дней, и при каждой загрузке страницы она меняется на access-токен.
 * Раньше ЛЮБАЯ неудача на этом шаге — сервер перезапускается после выкладки, база ответила
 * ошибкой, сеть моргнула — показывала экран входа, хотя сессия была цела: достаточно было
 * перезагрузить страницу. Человек этого не знает и начинает вводить пароль, а каждая ошибка в
 * пароле тратит попытку из суточного бюджета (разбор 17.09.2026).
 *
 * Теперь экран входа показывается только тогда, когда сервер прямо сказал, что сессии нет (401).
 * Всё остальное пережидается повторами — страница остаётся на «Загрузке», пока сервер не ответит.
 */

export type BootstrapResult = { status: 'authenticated'; me: MeResponse } | { status: 'anonymous' };

export interface BootstrapDeps {
  /** Обновить токен: `false` — сессии нет, исключение — сервер недоступен. */
  refresh: () => Promise<boolean>;
  fetchMe: () => Promise<MeResponse>;
  sleep: (ms: number) => Promise<void>;
  isCancelled: () => boolean;
}

/** Пауза перед повтором: 1, 2, 4, 8 секунд, дальше каждые 10. */
export function retryDelayMs(attempt: number): number {
  return Math.min(1000 * 2 ** attempt, 10_000);
}

/** `null` — компонент размонтирован, пока ждали сервер; результат никому не нужен. */
export async function bootstrapSession(deps: BootstrapDeps): Promise<BootstrapResult | null> {
  for (let attempt = 0; ; attempt += 1) {
    if (deps.isCancelled()) return null;
    try {
      const ok = await deps.refresh();
      if (!ok) return { status: 'anonymous' };
      const me = await deps.fetchMe();
      return { status: 'authenticated', me };
    } catch (err) {
      // Сессия мертва только на прямом 401 — его `fetchMe` получает, если токен отозвали между
      // обновлением и запросом профиля.
      if (err instanceof ApiError && err.status === 401) return { status: 'anonymous' };
    }
    await deps.sleep(retryDelayMs(attempt));
  }
}
